import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Plus, Loader2, AlertTriangle, CheckCircle2, Trash2 } from "lucide-react";
import { API } from "@/lib/api";


export default function VendorsPage() {
  const { authHeaders } = useAuth();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", industry: "", reward_logic: "spend_based", points_per_pound: 10,
    reward_threshold: 500, reward_value_gbp: 5, reward_expiry_days: 30,
  });

  const load = () => {
    setLoading(true);
    axios.get(`${API}/vendors`, { headers: authHeaders() }).then((r) => setVendors(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (e, vendorId, vendorName) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${vendorName}"? This will permanently remove the vendor and cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/vendors/${vendorId}`, { headers: authHeaders() });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete vendor");
    }
  };

  const handleToggleActive = async (e, vendor) => {
    e.stopPropagation();
    const next = !vendor.is_active;
    if (!next) {
      const ok = window.confirm(
        `Disable "${vendor.name}"? They (and their staff) will be unable to log in, use the API, or open the customer portal.`,
      );
      if (!ok) return;
    }
    try {
      await axios.put(
        `${API}/vendors/${vendor.id}`,
        { is_active: next },
        { headers: authHeaders() },
      );
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update vendor status");
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/vendors`, {
        ...form,
        points_per_pound: parseFloat(form.points_per_pound),
        reward_threshold: parseInt(form.reward_threshold),
        reward_value_gbp: parseFloat(form.reward_value_gbp),
        reward_expiry_days: parseInt(form.reward_expiry_days),
      }, { headers: authHeaders() });
      setOpen(false);
      setForm({ name: "", industry: "", reward_logic: "spend_based", points_per_pound: 10, reward_threshold: 500, reward_value_gbp: 5, reward_expiry_days: 30 });
      load();
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="vendors-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink tracking-tight">Vendors</h1>
          <p className="text-sm text-ink/50 mt-0.5">{vendors.length} registered vendor{vendors.length !== 1 ? "s" : ""}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-purple-500 hover:bg-purple-600 text-white h-9 text-sm" data-testid="create-vendor-button">
              <Plus size={14} /> Add Vendor
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Vendor</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest">Business Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest">Industry</Label>
                <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                  <SelectTrigger data-testid="vendor-industry-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="food_beverage">Food & Beverage</SelectItem>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="fitness">Fitness</SelectItem>
                    <SelectItem value="beauty">Beauty</SelectItem>
                    <SelectItem value="entertainment">Entertainment</SelectItem>
                    <SelectItem value="services">Services</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-widest">Points per £1</Label>
                  <Input type="number" value={form.points_per_pound} onChange={(e) => setForm({ ...form, points_per_pound: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-widest">Threshold (pts)</Label>
                  <Input type="number" value={form.reward_threshold} onChange={(e) => setForm({ ...form, reward_threshold: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-widest">Reward Value (£)</Label>
                  <Input type="number" step="0.01" value={form.reward_value_gbp} onChange={(e) => setForm({ ...form, reward_value_gbp: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-widest">Expiry (days)</Label>
                  <Input type="number" value={form.reward_expiry_days} onChange={(e) => setForm({ ...form, reward_expiry_days: e.target.value })} />
                </div>
              </div>
              <Button onClick={handleCreate} disabled={saving || !form.name || !form.industry} data-testid="create-vendor-submit" className="w-full bg-purple-500 hover:bg-purple-600 text-white">
                {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                Create Vendor
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-md border border-ink/10 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-surface">
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Vendor</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Industry</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Reward Logic</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Customers</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold w-36"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-ink/40 text-sm">Loading...</TableCell></TableRow>
            ) : vendors.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-ink/40 text-sm">No vendors yet</TableCell></TableRow>
            ) : (
              vendors.map((v) => (
                <TableRow
                  key={v.id}
                  className="cursor-pointer hover:bg-surface transition-colors"
                  onClick={() => navigate(`/vendors/${v.id}`)}
                  data-testid={`vendor-row-${v.id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-md bg-purple-50 flex items-center justify-center">
                        <Building2 size={14} className="text-ink" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-ink">{v.name}</p>
                        <p className="text-xs text-ink/40 font-mono">{v.api_key_prefix || v.api_key?.slice(0, 12) || "cl_…"}...</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-ink/60">{v.industry?.replace("_", " ")}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">{v.reward_logic?.replace("_", " ")}</Badge>
                  </TableCell>
                  <TableCell className="text-sm font-mono">{v.customer_count || 0}</TableCell>
                  <TableCell>
                    {v.is_active ? (
                      <Badge variant="outline" className="text-xs bg-purple-100 text-success border-success/30 gap-1">
                        <CheckCircle2 size={10} /> Active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs bg-danger/10 text-danger border-danger/30 gap-1">
                        <AlertTriangle size={10} /> Inactive
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs rounded-pill"
                        onClick={(e) => handleToggleActive(e, v)}
                        data-testid={`toggle-vendor-active-${v.id}`}
                      >
                        {v.is_active ? "Disable" : "Enable"}
                      </Button>
                      <button
                        onClick={(e) => handleDelete(e, v.id, v.name)}
                        className="p-1.5 rounded-lg text-ink/25 hover:text-danger hover:bg-danger/10 transition-colors"
                        data-testid={`delete-vendor-${v.id}`}
                        title="Delete vendor"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
